import {
  ensureApiJwtTokenRefreshed,
  getApiToken,
  isApiTokenLegacy,
} from '../../../../context/AuthContext';
import { getApiBaseUrl } from '../../../../utils/config';

const STORAGE_KEY_LOOM_JWT = 'loom-jwt';

const GAP_BEFORE_EXPIRED_SECONDS = 30 * 60;

type LoomJwtTokenInfo = {
  loomAppId: string;
  expiredAt: Date;
};

export const getLoomJwtToken = async (): Promise<string> => {
  let jwtToken: string | null = localStorage.getItem(STORAGE_KEY_LOOM_JWT);
  if (jwtToken == null || isExpired(jwtToken)) {
    const legacyApiToken = await getLegacyApiTokenOrRefreshJwt();
    if (!legacyApiToken) {
      await ensureApiJwtTokenRefreshed();
    }
    jwtToken = await getTokenFromServer(legacyApiToken);
    localStorage.setItem(STORAGE_KEY_LOOM_JWT, jwtToken);
  }
  return jwtToken;
};

const getLegacyApiTokenOrRefreshJwt = async (): Promise<string | undefined> => {
  const apiToken = await getApiToken();
  if (isApiTokenLegacy(apiToken)) {
    // if legacy api token, return it
    return apiToken ?? undefined;
  }
  // otherwise, ensure jwt token is refreshed
  return undefined;
};

// jwt will use cookie
const getTokenFromServer = (
  legacyApiToken: string | undefined,
): Promise<string> => {
  const url = getApiBaseUrl() + '/loom/jwt';
  return fetch(url, {
    headers: legacyApiToken
      ? {
          Authorization: legacyApiToken,
        }
      : undefined,
  }).then((resp) => resp.text());
};

const isExpired = (jwtToken: string): boolean => {
  const tokenInfo = getLoomJwtTokenInfo(jwtToken);
  if (tokenInfo === null) {
    return true;
  }
  const now = new Date();
  if (
    now.getTime() >=
    tokenInfo.expiredAt.getTime() - GAP_BEFORE_EXPIRED_SECONDS * 1000
  ) {
    return true;
  }
  return false;
};

export const getLoomJwtTokenInfo = (
  jwtToken: string,
): LoomJwtTokenInfo | null => {
  const payload = parseJwtPayload(jwtToken);
  if (!payload) {
    return null;
  }
  return {
    loomAppId: payload['iss'],
    expiredAt: new Date(payload['exp']),
  };
};

const parseJwtPayload = (jwtToken: string): Record<any, any> | null => {
  try {
    return JSON.parse(atob(jwtToken.split('.')[1]));
  } catch (e) {
    return null;
  }
};
